import React, { useState, useRef, useEffect } from 'react';
import { FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';
import './SuccessShowcase.css';

const SuccessShowcase = () => {
  const [currentSlide, setCurrentSlide] = useState(3); 
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [transform, setTransform] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const trackRef = useRef(null);
  const wrapperRef = useRef(null);

  // 7 сертификатов
  const certificates = [
    { id: 1, src: '/images/certs/module3-cert-1.webp', alt: 'User Success Score 1' },
    { id: 2, src: '/images/certs/module3-cert-2.webp', alt: 'User Success Score 2' },
    { id: 3, src: '/images/certs/module3-cert-3.webp', alt: 'User Success Score 3' },
    { id: 4, src: '/images/certs/module3-cert-4.webp', alt: 'User Success Score 4' },
    { id: 5, src: '/images/certs/module3-cert-5.webp', alt: 'User Success Score 5' },
    { id: 6, src: '/images/certs/module3-cert-6.webp', alt: 'User Success Score 6' },
    { id: 7, src: '/images/certs/module3-cert-7.webp', alt: 'User Success Score 7' },
  ];

  const totalSlides = certificates.length;

  const goToSlide = (index) => {
    if (index < 0) {
      setCurrentSlide(totalSlides - 1);
    } else if (index >= totalSlides) {
      setCurrentSlide(0);
    } else {
      setCurrentSlide(index);
    }
  };

  const nextSlide = () => goToSlide(currentSlide + 1);
  const prevSlide = () => goToSlide(currentSlide - 1);

  // Open modal
  const openModal = (cert) => {
    setModalImage(cert);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setModalImage(null);
    document.body.style.overflow = 'auto';
  };

  // Touch handlers
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) nextSlide();
    if (isRightSwipe) prevSlide();

    setTouchStart(0);
    setTouchEnd(0);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isModalOpen && e.key === 'Escape') {
        closeModal();
        return;
      }
      if (e.key === 'ArrowLeft') prevSlide();
      if (e.key === 'ArrowRight') nextSlide();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlide, isModalOpen]);

  // Calculate transform с responsive slideWidth
  const calculateTransform = () => {
    if (!trackRef.current) return 0;
    
    // Получаем все слайды
    const slides = trackRef.current.children;
    if (slides.length === 0) return 0;
    
    // Получаем центральный слайд
    const activeSlide = slides[currentSlide];
    if (!activeSlide) return 0;
    
    // Позиция активного слайда относительно track
    const slideLeft = activeSlide.offsetLeft;
    const slideWidth = activeSlide.offsetWidth;
    
    // Ширина wrapper (видимая область)
    const wrapperWidth = wrapperRef.current ? wrapperRef.current.offsetWidth : 0;
    
    // Центрируем активный слайд
    const offset = (wrapperWidth / 2) - (slideWidth / 2) - slideLeft;
    
    return offset;
  };

  // Update transform при смене слайда или resize
  useEffect(() => {
    const updateTransform = () => {
      setTransform(calculateTransform());
    };
    
    updateTransform();
    
    window.addEventListener('resize', updateTransform);
    return () => window.removeEventListener('resize', updateTransform);
  }, [currentSlide]);

  return (
    <>
      <section className="success-showcase">
        <div className="showcase-container">
          
          {/* Header */}
          <div className="showcase-header">
            <h2 className="showcase-title">Real Results</h2>
            <p className="showcase-subtitle">From exams to interviews — verified success stories</p>
          </div>

          {/* Carousel */}
          <div className="carousel-wrapper" ref={wrapperRef}>
            
            {/* Navigation Arrows */}
            <button 
              className="carousel-arrow carousel-arrow-left"
              onClick={prevSlide}
              aria-label="Previous certificate"
            >
              <FaChevronLeft />
            </button>

            {/* Slides Container */}
            <div 
              className="carousel-track"
              ref={trackRef}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              style={{ transform: `translateX(${transform}px)` }}
            >
              {certificates.map((cert, index) => (
                <div 
                  key={cert.id} 
                  className={`carousel-slide ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => {
                    if (index === currentSlide) {
                      openModal(cert);
                    } else {
                      goToSlide(index);
                    }
                  }}
                >
                  <div className="cert-frame">
                    <img 
                      src={cert.src} 
                      alt={cert.alt}
                      loading={index <= 2 ? 'eager' : 'lazy'}
                      className="cert-image"
                    />
                  </div>
                </div>
              ))}
            </div>

            <button 
              className="carousel-arrow carousel-arrow-right"
              onClick={nextSlide}
              aria-label="Next certificate"
            >
              <FaChevronRight />
            </button>
          </div>

          {/* Dots Pagination */}
          <div className="carousel-dots">
            {certificates.map((_, index) => (
              <button
                key={index}
                className={`dot ${index === currentSlide ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to certificate ${index + 1}`}
              />
            ))}
          </div>

          {/* Counter */}
          <div className="carousel-counter">
            {currentSlide + 1} / {totalSlides}
          </div>

        </div>
      </section>

      {/* Modal */}
      {isModalOpen && modalImage && (
        <div className="cert-modal-overlay" onClick={closeModal}>
          <button className="cert-modal-close" onClick={closeModal} aria-label="Close modal">
            <FaTimes />
          </button>
          <div className="cert-modal-content" onClick={(e) => e.stopPropagation()}>
            <img 
              src={modalImage.src} 
              alt={modalImage.alt}
              className="cert-modal-image"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default SuccessShowcase;
